import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// SPA scroll restoration.
//
// React Router v6 does NOT reset window.scrollY on <Link> navigation, so
// clicking "Self Doubling Growing Circle" (or any other <Link>) while the
// user is scrolled down on Landing left the destination page opened at
// the same offset — reproducible on desktop and mobile.
//
// We handle scroll manually on:
//   - PUSH  (regular forward navigation via <Link>): always jump to top.
//   - REPLACE (Navigate replace): same as PUSH.
//   - POP   (Back/Forward): leave alone so the browser can restore the
//           previous scroll position (default `history.scrollRestoration`
//           = "auto").
//
// If the destination has an explicit hash (e.g. /page#section), we skip
// the reset so anchor navigation still works. The window is also nudged
// once more on the next animation frame so late-mounting content (e.g.
// framer-motion reveal, Suspense boundaries) doesn't scroll us back down.
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  const lastPath = useRef(pathname);

  useEffect(() => {
    if (lastPath.current === pathname) return; // hash-only change / same route
    lastPath.current = pathname;

    if (navType === "POP") return;    // browser back/forward — respect history
    if (hash) return;                  // explicit anchor navigation

    // Two-phase reset: sync + next frame. The frame handler catches any
    // component that mounts after the router commits and grows the
    // document height (framer-motion, lazy sections).
    try { window.scrollTo(0, 0); } catch { /* jsdom / SSR */ }
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => {
        try { window.scrollTo(0, 0); } catch { /* noop */ }
      });
    }
  }, [pathname, hash, navType]);

  return null;
}
